import { memo, RefObject, useCallback, useMemo } from "react";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { useShallow } from "zustand/react/shallow";

import { calculateVirtualPadding } from "ds/components/Table/hooks/useStickyRow/helpers";
import useStickyRangeExtractor from "ds/components/Table/hooks/useStickyRow/useStickyRangeExtractor";

import IacManagementTableRowResource from "../Row/Resource";
import IacManagementTableRowGroup from "../Row/Group";
import IacManagementTableRowService from "../Row/Service";
import { RenderGroupRowContent, RenderResourceRowContent } from "../types";
import useIacTableStore from "../useIacTableStore";
import { selectTableRows } from "../useIacTableStore/tableDataSelectors";
import { selectStickyRanges } from "../useIacTableStore/tableMetaSelectors";

type IacManagementTableBodyProps = {
  tableWrapperRef: RefObject<HTMLDivElement | null>;
  renderGroupRow: RenderGroupRowContent;
  renderResourceRow: RenderResourceRowContent;
};

const IacManagementTableBody = ({
  tableWrapperRef,
  renderGroupRow,
  renderResourceRow,
}: IacManagementTableBodyProps) => {
  const { tableRows, stickyRanges, groupsMeta } = useIacTableStore(
    useShallow((state) => ({
      tableRows: selectTableRows(state),
      stickyRanges: selectStickyRanges(state),
      groupsMeta: state.groupsMeta,
    }))
  );

  const stickyRangesIndexes = useMemo(
    () =>
      stickyRanges.map<[number, number]>(([groupId, lastChildId]) => {
        const groupHasMore = groupsMeta[groupId]?.hasMore;
        const groupIndex = tableRows.findIndex((row) => row.id === groupId);
        const lastChildIndex = tableRows.findIndex((row) => row.id === lastChildId);

        // Include "Load More" button if group has more items
        return [groupIndex, groupHasMore ? lastChildIndex + 1 : lastChildIndex];
      }),
    [groupsMeta, stickyRanges, tableRows]
  );

  // render the sticky rows even if they are outside the virtual range
  const { rangeExtractor, stickyGroupsVisibleOnTableRef } =
    useStickyRangeExtractor(stickyRangesIndexes);

  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => tableWrapperRef.current,
    estimateSize: useCallback(
      (index: number) => {
        if ("serviceType" in tableRows[index]) return 96;
        return 57;
      },
      [tableRows]
    ),
    overscan: 30,
    rangeExtractor,
  });

  const virtualizedItems = virtualizer.getVirtualItems();

  // calculate before & after padding to keep the right scroll position
  const [before, after] = useMemo(() => {
    if (!tableRows.length || !virtualizedItems.length) {
      return [0, 0];
    }

    return calculateVirtualPadding(
      virtualizedItems,
      virtualizer.getTotalSize(),
      virtualizer.options.scrollMargin,
      stickyGroupsVisibleOnTableRef.current.size
    );
  }, [virtualizedItems, tableRows, virtualizer, stickyGroupsVisibleOnTableRef]);

  const renderRow = useCallback(
    (virtualRow: VirtualItem | undefined) => {
      if (!virtualRow) return null;

      const row = tableRows[virtualRow.index];

      // Group Row
      if ("count" in row) {
        // the key points to the first group index
        // the stickyGroup value points to the last group child index
        const stickyGroup = stickyGroupsVisibleOnTableRef.current.get(virtualRow.index);
        const nextGroupAfterSticky = stickyGroup
          ? virtualizer.measurementsCache[stickyGroup + 1]
          : undefined;
        // the value to detect sticky overlapping starts
        const scrollTopTarget = nextGroupAfterSticky
          ? nextGroupAfterSticky.start - virtualizer.measurementsCache[virtualRow.index]?.size + 1
          : undefined;

        return (
          <IacManagementTableRowGroup
            key={virtualRow.key}
            rowIndex={virtualRow.index}
            measureRef={virtualizer.measureElement}
            isActiveSticky={!!stickyGroup}
            nextAfterSticky={scrollTopTarget}
            tableRef={tableWrapperRef}
            renderContent={renderGroupRow}
          />
        );
      }

      // Service Row (e.g load more button)
      if ("serviceType" in row) {
        return (
          <IacManagementTableRowService
            key={virtualRow.key}
            rowIndex={virtualRow.index}
            measureRef={virtualizer.measureElement}
          />
        );
      }

      // Resource Row
      return (
        <IacManagementTableRowResource
          key={virtualRow.key}
          rowIndex={virtualRow.index}
          measureRef={virtualizer.measureElement}
          renderContent={renderResourceRow}
        />
      );
    },
    [
      tableRows,
      virtualizer.measureElement,
      virtualizer.measurementsCache,
      renderResourceRow,
      tableWrapperRef,
      renderGroupRow,
      stickyGroupsVisibleOnTableRef,
    ]
  );

  return (
    <div style={{ display: "table-row-group" }} role="rowgroup">
      {before > 0 && <div style={{ display: "table-row", height: before }}></div>}
      {virtualizedItems.map(renderRow)}
      {after > 0 && <div style={{ display: "table-row", height: after }}></div>}
    </div>
  );
};

export default memo(IacManagementTableBody);
